import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

// Uploads a MediaRecorder's chunks to storage every 20s, then asks the server to join them at the end.
export function useRecordingParts(kind, getSessionId, chunksRef) {
  const st = useRef({ sent: 0, seq: 0, bytes: 0, busy: false, attempt: Math.floor(Date.now() / 1000) });
  const timer = useRef(null);

  const uploadPart = async () => {
    const s = st.current, sid = getSessionId();
    if (s.busy || !sid || chunksRef.current.length <= s.sent) return;
    s.busy = true;
    const end = chunksRef.current.length;
    const part = new Blob(chunksRef.current.slice(s.sent, end), { type: "video/webm" });
    const fd = new FormData();
    fd.append("file", part, `part-${s.seq}.webm`);
    try {
      await api.post(`/recordings/part/${kind}/${sid}?attempt=${s.attempt}&seq=${s.seq}`, fd, { timeout: 60000 });
      s.sent = end; s.seq += 1; s.bytes += part.size;
    } catch (_) { /* retried on the next tick */ }
    s.busy = false;
  };

  const start = () => { clearInterval(timer.current); timer.current = setInterval(uploadPart, 20000); };
  const stop = () => clearInterval(timer.current);
  useEffect(() => stop, []);

  // Call after the recorder has stopped. Returns true when the server has the full recording.
  const finish = async (totalBytes, onProgress) => {
    stop();
    const s = st.current;
    try {
      for (let i = 0; i < 60 && s.busy; i++) await new Promise((r) => setTimeout(r, 500));
      for (let tries = 0; tries < 4 && chunksRef.current.length > s.sent; tries++) {
        await uploadPart();
        onProgress?.({ pct: Math.round((s.bytes / totalBytes) * 100), loaded: s.bytes, total: totalBytes });
        if (chunksRef.current.length > s.sent) await new Promise((r) => setTimeout(r, 1500 * (tries + 1)));
      }
      if (chunksRef.current.length > s.sent || s.seq === 0) return false;
      await api.post(`/recordings/finalize/${kind}/${getSessionId()}?attempt=${s.attempt}&parts=${s.seq}`, null, { timeout: 180000 });
      onProgress?.({ pct: 100, loaded: totalBytes, total: totalBytes });
      return true;
    } catch (_) {
      return false;
    }
  };

  return { start, stop, finish };
}
